/* Angular modules */
import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
/* ngx modules */
import {SessionStorage} from 'ngx-webstorage';
import {TranslateService} from '@ngx-translate/core';
import {TreeNode} from 'primeng/api';
import {MessageService} from 'primeng/api';
/* app modules */
import {NavigationStep} from '../../shared/guards/navigationStep-enum';
import {BomService} from '../shared';
import {ItemService} from '../../project/shared/item.service';
import {Item, Project, Status} from '../../project/shared/project-model';
import {CommonModalComponent} from '../../shared/common-modal/common-modal.component';
import {UtilService} from '../../shared/util/util.service';
import {LoggerService} from '../../shared/logging/logger.service';
import {RightsService} from '../../shared/rights/rights.service';
import {ReferenceBom} from '../shared/bom-model';
import {User} from '../../shared/user/user';
import {BomHelper} from '../shared';
import {ProjectService} from '../../project/shared/project.service';
import {BomTreeDataHelper} from '../shared/bom-tree-data-helper.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MySEStatusEnum} from '../../shared/model/mySEStatusEnum';
import {NavigationRoute} from '../../shared/guards/route-enum';
import {Range} from '../../shared/model/range-model';
import {OfferService} from '../../offers/shared/offer.service';
import {Role} from '../../core/access-control/enum';
import {FeedbackService} from '../../feedback/shared/service/feedback.service';
import {environment} from '../../../environments/environment';
import {ViewCartBomEventDatalayerService} from '../../shared/google-analytics/view-cart-bom-event-datalayer.service';
import {RangeType} from "../../shared/model/range-type";
import {
  ConfigurationEventDatalayerService
} from "../../shared/google-analytics/configuration-start-completed-datalayer.service";

@Component({
  standalone: false,
  selector: 'app-bom-page',
  templateUrl: './bom-page.component.html',
  styleUrls: ['./bom-page.component.less'],
})
export class BomPageComponent implements OnInit, OnDestroy, AfterViewInit {

  @SessionStorage()
  currentItemct: Item;

  @SessionStorage()
  currentProject: Project;

  @SessionStorage()
  localization;

  @SessionStorage()
  gaBomTrigger: boolean;

  @SessionStorage()
  user: User;

  // FIXME: Find a logic to have a single parameter (either isDemoUser or isDemoMode)
  @SessionStorage()
  isDemoMode: boolean;

  @SessionStorage()
  hasRegister: boolean;

  loadingBom = false;
  loadingQuotationBom = false;
  expandedNodes: string[] = [];
  bomString: string = null;
  status = Status;
  unsubscribe$: Subject<void> = new Subject<void>();
  validityPeriod: number;
  ranges: Range[];

  @ViewChild('quoteModal') quoteModal: CommonModalComponent;
  @ViewChild('updateQuoteModal') updateQuoteModal: CommonModalComponent;

  public cols: Array<any>;

  treeData: TreeNode[] = [];

  public constructor(private bomService: BomService,
                     private bomHelper: BomHelper,
                     private bomTreeDataHelper: BomTreeDataHelper,
                     private itemService: ItemService,
                     public utilService: UtilService,
                     private logger: LoggerService,
                     private projectService: ProjectService,
                     private translateService: TranslateService,
                     private rightsService: RightsService,
                     private messageService: MessageService,
                     private offerService: OfferService,
                     private feedbackService: FeedbackService,
                     private configurationEventDatalayerService: ConfigurationEventDatalayerService,
                     private viewCartBomEventDatalayerService: ViewCartBomEventDatalayerService) {
  }

  public ngOnInit(): void {
    this.logger.debug('BomPageComponent init()');

    this.offerService.getRangesBackOffice()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(ranges => {
        this.ranges = ranges;

        this.currentItemct.currentNavigationStep = NavigationStep.BILL_OF_MATERIALS;
        this.bomService.extractReferencesToArray(this.currentItemct, false);

        if (this.currentItemct.status === Status.configured) {
          this.configurationEventDatalayerService.ConfigurationCompletedEvent();
        }

        // In the case of CPQ user, we allow the Documentation step
        if (this.projectService.isCpqProject(this.currentProject)) {
          this.showFinalizedConfigurationFeedback();
          this.currentItemct.maxNavigationStep = this.currentItemct.range ?
            NavigationRoute.getNextStepByRange(this.currentItemct.range, this.currentItemct.currentNavigationStep).id :
            NavigationRoute.getNextStepByPackageOffer(this.currentItemct.currentNavigationStep).id;
          if (this.currentItemct.status === Status.ordered) {
            // If CPQ project is already ordered, we do not update the status
            this.itemService.updateItem(this.currentItemct, this.user)
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe(res => {
                this.currentItemct = res;
              });
          } else {
            this.gaBomTrigger = false;
            this.generateBomInformation(this.currentItemct);
            // Otherwise, we update status
            this.itemService.updateItemWithStatusChange(this.currentItemct, Status.quoted, this.user)
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe(res => {
                this.currentItemct = res;
              });
          }
        }

        this.cols = this.bomHelper.getBOMColumns(this.isDemoMode, this.isCpqProject(),
          this.rightsService.canApplyDiscount(), this.isMySEUser(),
          this.isPartnerNetPrice(),
          this.displayAvailability(), this.rightsService.isPricesDetailsHidden());

        if (this.currentItemct.status === Status.configured) {
          this.updateBom();
        } else {
          this.itemService.setItemNavigationStep(NavigationStep.BILL_OF_MATERIALS, this.user, this.unsubscribe$);
          this.gaBomTrigger = true;
          this.generateBomInformation(this.currentItemct);
        }
        this.validityPeriod = this.bomService.getValidityPeriod(this.currentItemct, this.ranges);
      });
  }

  ngAfterViewInit(): void {
    if (this.currentItemct.status === Status.quoted && !this.currentProject.readOnly && !this.projectService.isCpqProject(this.currentProject)) {
      this.offerService.getRangesBackOffice()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(ranges => {
          this.ranges = ranges; // make sure the ranges are not null before QuotationValid checking
          this.isQuotationValid();
        });
    }
  }

  checkQuotation(): void {
    if (!this.projectService.isCpqProject(this.currentProject)) {
      if (this.currentItemct.status === Status.configured && !this.currentProject.readOnly) {
        this.quoteModal.show();
      }
    }
  }

  /**
   * Unsubscribe observables
   */
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    // unsubscribe from the subject itself:
    this.unsubscribe$.unsubscribe();
  }

  public isAdminUser(): boolean {
    return this.rightsService.isNationalSales();
  }

  isDemoUser(): boolean {
    return this.rightsService.isActivatedDemo();
  }


  /**
   * Return true if the user is a mySE user
   * @returns {boolean}
   */
  isMySEUser(): boolean {
    return !!this.user.currentMySEAccount;
  }

  isReadOnlyProject(): boolean {
    return this.projectService.isReadOnlyProject(this.currentProject, this.user);
  }

  /**
   * Get BOM Tree Data from the current item
   * @param item
   * @param quoteItem
   */
  generateBomInformation(item: Item, quoteItem?: boolean) {
    if (this.currentItemct.status === Status.configured && (!this.currentItemct.range || this.currentItemct.range.refs !== null || this.user.currentMySEAccount)) {
      // We log finalize configuration for configured items
      this.logBomInfo(item);
    }
    // deep clone of the item
    const myItem = UtilService.clone(item);
    this.treeData = this.bomTreeDataHelper.extractReferencesToTree(myItem, this.ranges);
    this.treeData.forEach(value => {
      // restore expanded state
      value.expanded = true;
      // first category: accessories, cubicles, transformers
      value.data.totalPrice = UtilService.formatPrice(value.data.totalPrice);
      value.data.totalNetPrice = UtilService.formatPrice(value.data.totalNetPrice);
      value.data.discountedNetPrice = UtilService.formatPrice(value.data.discountedNetPrice);
      value.data.discountedPrice = UtilService.formatPrice(value.data.discountedPrice);
      // set price in empty if demo mode
      if (this.isDemoMode) {
        value.data.totalPrice = '';
        value.data.totalNetPrice = '';
        value.data.price = '';
      }
      this.bomHelper.formatTreeNode(value, this.isDemoMode);
    });
    if (this.gaBomTrigger === true) {
      this.viewCartBomEventDatalayerService.viewBomEvent(item, this.currentProject, this.localization.currency, this.treeData);
      this.gaBomTrigger = false;
    }
    if (quoteItem) {
      this.quoteCurrentItem();
    }
  }

  isContractor(): boolean {
    return this.rightsService.isContractor();
  }

  /**
   * Method to update status item to Quote
   */
  public quoteCurrentItem() {
    this.logger.info('BomPageComponent quoteCurrentItem()');
    this.currentItemct.maxNavigationStep = this.currentItemct.range ?
      NavigationRoute.getNextStepByRange(this.currentItemct.range, this.currentItemct.currentNavigationStep).id :
      NavigationRoute.getNextStepByPackageOffer(this.currentItemct.currentNavigationStep).id;
    const now = new Date();
    this.loadingQuotationBom = true;
    this.currentItemct.date = now.getTime();
    // Validity period of quotation
    const validityPeriod = this.bomService.getValidityPeriod(this.currentItemct, this.ranges);
    // Compute expiration date of quotation
    const futureDate = new Date(this.currentItemct.date);
    futureDate.setDate(futureDate.getDate() + validityPeriod)
    // Set quotation validity date
    this.currentItemct.dateValidityQuotation = futureDate.valueOf();
    this.itemService.updateItemWithStatusChange(this.currentItemct, Status.quoted, this.user)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(res => {
          this.currentItemct = res;
          this.loadingQuotationBom = false;
          this.gaBomTrigger = false;
          this.generateBomInformation(this.currentItemct);
          const quotePayload: any = {
            currency: this.localization.currency
          };
          if (!this.rightsService.isActivatedDemo()) {
            quotePayload.total_price = UtilService.getFormattedTotalDiscountPriceFromBOM(this.currentItemct.bom,
              this.user.currentMySEAccount === null && this.currentItemct.totalPrice.validFoNetPrice,
              this.currentItemct.discount);
          }
          this.logger.business('Quote configuration', quotePayload);
          // this.updateBom();
        },
        () => {
          this.loadingQuotationBom = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('T_ERROR'),
            detail: this.translateService.instant('T_ITEM_UPDATE_ERROR'),
          });
        });

    this.showFinalizedConfigurationFeedback();
  }

  updateBom(quoteItem?: boolean) {
    this.loadingBom = true;
    if (!!this.currentItemct.range && this.currentItemct.range.refs === null
      && !this.user.currentMySEAccount) {
      this.loadingBom = false;
      // Synchro called to not lose the current navigation step
      this.itemService.setItemNavigationStep(NavigationStep.BILL_OF_MATERIALS, this.user, this.unsubscribe$);
      this.generateBomInformation(this.currentItemct);
    } else {
      this.bomService.getBom(this.currentItemct.id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(item => {
            this.currentItemct = item;
            this.cols = this.bomHelper.getBOMColumns(this.isDemoMode, this.isCpqProject(),
              this.rightsService.canApplyDiscount(), this.isMySEUser(),
              this.isPartnerNetPrice(),
              this.displayAvailability(), this.rightsService.isPricesDetailsHidden());
            this.loadingBom = false;
            if (!quoteItem) {
              this.checkQuotation();
            }
            // Synchro called to not lose the current navigation step
            this.itemService.setItemNavigationStep(NavigationStep.BILL_OF_MATERIALS, this.user, this.unsubscribe$, true);
            this.gaBomTrigger = true;
            this.generateBomInformation(item, quoteItem);
            this.logBomInfo(item);
          },
          () => {
            this.loadingBom = false;
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('T_ERROR'),
              detail: this.translateService.instant('T_ITEM_UPDATE_ERROR'),
            });
          });
    }
  }

  /**
   * Method to know if it is a CPQ project
   * @returns {boolean}
   */
  isCpqProject(): boolean {
    return this.projectService.isCpqProject(this.currentProject);
  }

  isCpqUser(): boolean {
    return this.user.role == Role.CPQ_USER;
  }

  /**
   * Check if it is necessary to display availability on stock column
   */
  private displayAvailability(): boolean {
    let display;
    // Check components
    display = this.currentItemct.components.some(sw => {
      if (sw.reference !== null && sw.reference.availability !== null) {
        return true;
      }
      // Check options of switchboard
      return sw.options.some(opt => {
        return opt.values.some(val => val.selected && val.reference !== null && val.reference.availability !== null);
      }); // opt
    }); // component

    // Check accessories
    display = display || this.currentItemct.accessories.some(acc => {
      return acc.reference !== null && acc.reference.availability !== null;
    });

    // Check Packing
    display = display || this.currentItemct.optionalPacking.filter(packing => packing.values !== null).some(packing => {
      return packing.values.some(val => {
        return (val.reference !== null && val.reference.availability !== null);
      });
    });
    return display;
  }

  /**
   * Check if BOM contains null public prices
   */

  bomContainsNullPublicPrices() {
    const isPricesDetailsHidden = this.rightsService.isPricesDetailsHidden();
    const canApplyDiscount = this.rightsService.canApplyDiscount();
    return this.treeData.some(treeNode => {
      if (isPricesDetailsHidden) {
        const hasPublicNullPrices = this.hasNullPricesInChildren(treeNode.children);
        return hasPublicNullPrices;
      }
      if (treeNode.data.totalPrice === 'containsNullPrices') {
        return canApplyDiscount ? this.hasNullPricesInChildren(treeNode.children) : true;
      }
      return false;
    });
  }

  /**
   * Check if BOM contains null net prices
   */
  bomContainsNullNetPrices() {
    const isPricesDetailsHidden = this.rightsService.isPricesDetailsHidden();
    const canApplyDiscount = this.rightsService.canApplyDiscount();
    return this.treeData.some(treeNode => {
      if (isPricesDetailsHidden) {
        const hasNetNullPrices = this.checkChildNodeForNullPrices(treeNode.children, canApplyDiscount);
        if ((treeNode.data.totalNetPrice === '0.00' || treeNode.data.totalNetPrice === 'containsNullPrices') && hasNetNullPrices) {
          treeNode.data.totalNetPrice = 'containsNullPrices';
          return true;
        }
        return hasNetNullPrices;
      }
      if (treeNode.data.totalNetPrice === 'containsNullPrices') {
        return canApplyDiscount ? this.hasNullPricesInChildren(treeNode.children) : true;
      }
      return false;
    });
  }

  /**
   * Check if there are null prices in children nodes
   * @param node
   * @private
   */
  private hasNullPricesInChildren(node: TreeNode[]): boolean {
    const included = this.translateService.instant('T_BOM_INCLUDED');
    return node.some(childNode => {
      if (childNode.data.price === null) return true;
      if (childNode.data.price === included) return false;
      return childNode.children.some(grandChildNode => {
        if (grandChildNode.data.price === null) return true;
        if (grandChildNode.data.price === included) return false;
        return grandChildNode.children.some(grandGrandChildNode => {
          if (grandGrandChildNode.data.price === null) return true;
          if (grandGrandChildNode.data.price === included) return false;
        });
      });
    });
  }

  /**
   * Check if child node has null prices
   * @param children
   * @param canApplyDiscount
   * @private
   */
  private checkChildNodeForNullPrices(children: TreeNode[], canApplyDiscount: boolean): boolean {
    const priceKey = canApplyDiscount ? 'price' : 'netPrice';
    const included = this.translateService.instant('T_BOM_INCLUDED');
    return children.some(childNode => {
      if (childNode.data[priceKey] === null) return true;
      if (childNode.data[priceKey] === included) return false;
      return childNode.children.some(grandChildNode => {
        if (grandChildNode.data[priceKey] === null) return true;
        if (grandChildNode.data[priceKey] === included) return false;
        return grandChildNode.children.some(grandGrandChildNode => {
          if (grandGrandChildNode.data[priceKey] === null) return true;
          if (grandGrandChildNode.data[priceKey] === included) return false;
        });
      });
    });
  }

  /**
   * Returns true if item has every price > zero
   * Returns false otherwise
   * @param item
   */
  private hasEveryPrices(item: Item): boolean {
    let hasAllPrices;
    // Check components
    hasAllPrices = item.components.every(sw => sw.reference !== null && sw.reference.price > 0 &&
      sw.options.every(opt => {
        const optValues = opt.values.filter(val => val.selected && val.reference != null);
        return optValues.length === 0 || optValues.every(val => val.selected && val.reference !== null && val.reference.price > 0);
      }));

    // Check accessories
    hasAllPrices = hasAllPrices && item.accessories.filter(acc => acc.reference !== null)
      .every(acc => acc.reference !== null && acc.reference.price > 0);

    // Check Packing
    hasAllPrices = hasAllPrices && item.optionalPacking.filter(packing => packing.values != null).every(packing =>
      packing.values.some(val => val.reference !== null && val.reference.price > 0));

    // Check Services
    hasAllPrices = hasAllPrices && item.selectedServices
      .every(service => service.reference !== null && service.reference.price > 0);

    return hasAllPrices;
  }

  /**
   * Returns true if item has at least one price > 0
   * Returns false otherwise
   * @param item
   */
  private hasSomePrices(item: Item): boolean {
    let hasPrice;
    // Check components
    hasPrice = item.components.some(sw => {
      if (sw.reference !== null && sw.reference.price > 0) {
        return true;
      }
      // Check options of switchboard
      return sw.options.some(opt => {
        return opt.values.some(val => val.selected && val.reference !== null && val.reference.price > 0);
      }); // opt
    }); // component

    // Check accessories
    hasPrice = hasPrice || item.accessories.some(acc => {
      return acc.reference !== null && acc.reference.price > 0;
    });

    // Check Packing
    hasPrice = hasPrice || item.optionalPacking.filter(packing => packing.values !== null).some(packing => {
      return packing.values.some(val => {
        return (val.reference !== null && val.reference.price > 0);
      });
    });

    // Check Services
    hasPrice = hasPrice || item.selectedServices.some(service => {
      return service.reference !== null && service.reference.price > 0;
    });
    return hasPrice;
  }


  /**
   * Check if it has at least one reference with net price
   */
  private someNetPrice(): boolean {
    let hasNetPrice;
    // Check components
    hasNetPrice = this.currentItemct.components.some(sw => {
      if (sw.reference !== null && sw.reference.mySeNetPriceStatus === MySEStatusEnum.OK) {
        return true;
      }
      // Check options of switchboard
      return sw.options.some(opt => {
        return opt.values.some(val => val.selected && val.reference !== null && val.reference.mySeNetPriceStatus === MySEStatusEnum.OK);
      }); // opt
    }); // component

    // Check accessories
    hasNetPrice = hasNetPrice || this.currentItemct.accessories.some(acc => {
      return acc.reference !== null && acc.reference.mySeNetPriceStatus === MySEStatusEnum.OK;
    });

    // Check Packing
    hasNetPrice = hasNetPrice || this.currentItemct.optionalPacking.filter(packing => packing.values !== null).some(packing => {
      return packing.values.some(val => {
        return (val.reference !== null && val.reference.mySeNetPriceStatus === MySEStatusEnum.OK);
      });
    });

    // Check Services
    hasNetPrice = hasNetPrice || this.currentItemct.selectedServices.some(service => {
      return service.reference !== null && service.reference.mySeNetPriceStatus === MySEStatusEnum.OK;
    });
    return hasNetPrice;
  }

  private logBomInfo(itemProject: Item) {
    setTimeout(() => {
      let totalPublicPrice = this.currentItemct.totalPrice.foPublicPrice;
      let totalNetPrice = totalPublicPrice;
      const refArray: ReferenceBom[] = this.bomService.extractReferencesToArray(itemProject);
      const refDtoArray = refArray.map(
        (referenceBom: ReferenceBom) =>
          'REFERENCE :' + referenceBom.ref + ' REFERENCE_DESCRIPTION : ' + referenceBom.description + ' QUANTITY ' + referenceBom.count);
      this.bomString = refDtoArray.toString();
      let configPriceStatus: string;
      if (this.isMySEUser()) {
        if (itemProject.mySeNetPriceStatus === MySEStatusEnum.OK && itemProject.totalPrice.mySENetPrice > 0) {
          configPriceStatus = 'Full Price';
          totalNetPrice = this.currentItemct.totalPrice.mySENetPrice;
        } else if (this.someNetPrice() && itemProject.totalPrice.mySENetPrice > 0) {
          configPriceStatus = 'Partial Price';
        } else {
          configPriceStatus = 'No Price';
        }
      } else {
        // Case of database prices
        if (this.hasEveryPrices(itemProject)) {
          configPriceStatus = 'Full Price';
        } else if (this.hasSomePrices(itemProject)) {
          configPriceStatus = 'Partial Price';
        } else {
          configPriceStatus = 'No Price';
        }
      }
      if (totalPublicPrice === null || totalPublicPrice === undefined) {
        totalPublicPrice = 0;
      }
      // We add the discount for net price
      totalNetPrice = UtilService.applyDiscountPrice(totalNetPrice, itemProject.discount);
      const finalisePayload: any = {
        currency: this.localization.currency,
        bom_references: this.bomString,
        config_price_status: configPriceStatus
      };
      if (!this.rightsService.isActivatedDemo()) {
        finalisePayload.total_public_price = totalPublicPrice;
        finalisePayload.total_net_price = totalNetPrice;
      }
      this.logger.business('Finalise configuration', finalisePayload);

    }, 1000);
  }

  private isQuotationValid() {
    if (this.currentItemct) {
      const validityPeriod = this.bomService.getValidityPeriod(this.currentItemct, this.ranges);
      const expirationDate = new Date(this.currentItemct.date);
      expirationDate.setDate(expirationDate.getDate() + validityPeriod);
      const quoteIsValid = expirationDate > new Date();
      if (!quoteIsValid) {
        this.updateQuoteModal.show();
      }
    }
  }

  /**
   * Return true if current item has a partner net price
   * If there is no total price, return false
   */
  private isPartnerNetPrice() {
    return this.currentItemct.totalPrice ? this.currentItemct.totalPrice.validFoNetPrice : false;
  }

  /**
   * Show feedback modal if the last feedback is expired for finalized configuration event
   */
  private showFinalizedConfigurationFeedback() {
    this.feedbackService.showEventFeedbackModal(environment.feedbackDelay * 1000); // show feedback after x seconds
  }

}
